import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Pagination,
    InputAdornment,
    Grid
} from '@mui/material';
import {
    Search as SearchIcon,
    People as PeopleIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const SuperAdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
    const [search, setSearch] = useState('');
    const [role, setRole] = useState('');
    const [status, setStatus] = useState('');

    const fetchUsers = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (search) params.append('search', search);
            if (role) params.append('role', role);
            if (status) params.append('status', status);

            const response = await api.get(`/super-admin/users?${params.toString()}`);
            setUsers(response.data.users);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error('Failed to load users:', err);
        } finally {
            setLoading(false);
        }
    }, [search, role, status]);

    useEffect(() => {
        fetchUsers(1);
    }, [fetchUsers]);

    const handlePageChange = (event, page) => {
        fetchUsers(page);
    };

    const hasActiveFilters = search || role || status;

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getRoleColor = (r) => {
        switch (r) {
            case 'admin': return 'primary';
            case 'cashier': return 'success';
            case 'branch_admin': return 'info';
            case 'viewer': return 'default';
            case 'business_owner': return 'secondary';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    User Management
                </Typography>
            </Box>

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{ p: 2.5, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FilterIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="subtitle2" color="text.secondary">Filters</Typography>
                    {hasActiveFilters && (
                        <Typography
                            variant="caption"
                            sx={{ ml: 'auto', cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => { setSearch(''); setRole(''); setStatus(''); }}
                        >
                            Clear All
                        </Typography>
                    )}
                </Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="business_owner">Business Owner</MenuItem>
                                <MenuItem value="branch_admin">Branch Admin</MenuItem>
                                <MenuItem value="cashier">Cashier</MenuItem>
                                <MenuItem value="viewer">Viewer</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="deactivated">Deactivated</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Users Table */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}
            >
                <Box sx={{
                    px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        {pagination.total} {pagination.total === 1 ? 'user' : 'users'} found
                    </Typography>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {['Name', 'Email', 'Role', 'Business', 'Status', 'Joined', 'Last Login'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center', borderBottom: 'none' }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center', borderBottom: 'none' }}>
                                        <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            No users found
                                        </Typography>
                                        <Typography variant="body2" color="text.disabled">
                                            {hasActiveFilters ? 'Try adjusting your filters' : 'Users will appear here once businesses register'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => (
                                    <TableRow key={user.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.role.replace('_', ' ')}
                                                size="small"
                                                color={getRoleColor(user.role)}
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {user.businessName ? (
                                                <Box>
                                                    <Typography variant="body2">{user.businessName}</Typography>
                                                    {!user.businessActive && (
                                                        <Chip label="Inactive" size="small" color="error" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5 }} />
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">No business</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.status}
                                                size="small"
                                                color={user.status === 'active' ? 'success' : 'error'}
                                                sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(user.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color={user.lastLogin ? 'text.secondary' : 'text.disabled'}>
                                                {formatDateTime(user.lastLogin)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {pagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            size="medium"
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default SuperAdminUserManagement;
